import { ISystemOverviewResponse } from '@kcftech/prometheus-client';
import { FavoriteButton } from 'components/favorites/FavoriteButton';
import { HardwareIcon } from 'components/svg_icon/HardwareIcon';
import { Tile } from 'components/tiles/Tile';
import { usePermissions } from 'contexts/PermissionsContext';
import { SettingsActionsMenu } from 'pages/workbench/SettingsActionsMenu';
import { useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { getSystemWorkbenchTab, WorkbenchTab } from 'utils/RouteHelper';

export type SystemTileProps = Readonly<{
    system: ISystemOverviewResponse;
    onGenerateAlarmThresholds?: () => void;
    onAcknowledgeAlerts: () => void;
    onEdit: () => void;
    onDelete: () => void;
}>;

const AssetAlertsNumber = styled.div<{
    $assetsWithAlerts: number;
}>`
    border-radius: 8px;
    background: ${({ $assetsWithAlerts }) =>
        $assetsWithAlerts > 0 ? tokens.error.light : tokens.success.default};
    color: ${tokens.foreground.inverse};
    width: 25px;
    height: 15px;
    margin-bottom: -15px;
    margin-left: 20px;
    position: relative;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Title = styled.div`
    grid-area: title;
`;

const Assets = styled.div`
    grid-area: assets;
    align-self: end;
`;

const Actions = styled.div`
    grid-area: actions;
`;

const Favorite = styled(FavoriteButton)`
    grid-area: favorite;
    align-self: end;
`;

const TileGrid = styled.div`
    display: grid;
    height: 100px;
    width: 100%;
    column-gap: 10px;
    grid-template-rows: auto auto;
    grid-template-columns: 1fr 8fr 1fr;
    grid-template-areas:
        'title title actions'
        'assets assets favorite';
`;

export function SystemTile({
    system,
    onGenerateAlarmThresholds,
    onAcknowledgeAlerts,
    onEdit,
    onDelete,
}: SystemTileProps) {
    const { canEditSystem, canDeleteSystem } = usePermissions();
    const assetsWithAlerts = useMemo(
        () =>
            system.AssetStatusCount
                ? system.AssetStatusCount.CriticalAlarm +
                  system.AssetStatusCount.Alarm +
                  system.AssetStatusCount.Warning
                : 0,
        [system],
    );

    return (
        <Tile
            navigateTo={getSystemWorkbenchTab(system.Id, WorkbenchTab.Trend)}
            className="p-18 overflow-hidden tile-page-container"
            data-cy="system-tile"
        >
            <TileGrid>
                <Title className="bold word-wrap-anywhere">{system.Name}</Title>
                <Assets>
                    <AssetAlertsNumber
                        $assetsWithAlerts={assetsWithAlerts}
                        title="Number of Assets currently with Alerts"
                        aria-label="Number of Assets currently with Alerts"
                    >
                        <span>{assetsWithAlerts}</span>
                    </AssetAlertsNumber>
                    <HardwareIcon type="asset" color={tokens.icon.default} size={30} />
                </Assets>
                <Favorite favoriteItem={system} favoriteType="System" />
                <Actions className="flex-column justify-between align-center">
                    <SettingsActionsMenu
                        onSettingsClick={onEdit}
                        onGenerateAlarmThresholdsClick={onGenerateAlarmThresholds}
                        onAcknowledgeAlertsClick={onAcknowledgeAlerts}
                        onDeleteClick={onDelete}
                        canEdit={canEditSystem}
                        canDelete={canDeleteSystem}
                    />
                </Actions>
            </TileGrid>
        </Tile>
    );
}
